import { z } from 'zod';
import { CERTIFICATE, CERTIFICATE_NUMBER_PATTERN, CERTIFICATE_STATUSES, CERTIFICATE_VERDICTS, CERTIFICATE_VERIFY_CODE_PATTERN, } from '../../constants/index.js';
import { objectIdSchema } from '../common.js';
import { auditableSchema } from './base.js';
export const certificateStatusSchema = z.enum(CERTIFICATE_STATUSES);
export const certificateVerdictSchema = z.enum(CERTIFICATE_VERDICTS);
/**
 * F.I.Sh. va kurs nomi — ODDIY SATR, `TranslatableString` EMAS.
 *
 * Odamning ismi tarjima qilinmaydi: qog'ozdagi sertifikatda u bitta
 * ko'rinishda bosilgan va tekshiruvchi aynan shu yozuvni solishtiradi.
 */
const personNameSchema = z
    .string()
    .trim()
    .min(3, 'F.I.Sh. kamida 3 belgi')
    .max(200, 'F.I.Sh. 200 belgidan oshmasligi kerak');
export const certificateNumberSchema = z
    .string()
    .trim()
    .regex(CERTIFICATE_NUMBER_PATTERN, 'Seriya raqami noto‘g‘ri: faqat harf, raqam, «-» va «/»');
export const certificateHoursSchema = z.coerce
    .number()
    .int('Soat butun son bo‘lishi kerak')
    .min(CERTIFICATE.MIN_HOURS, 'Soat hajmi kamida 1')
    .max(CERTIFICATE.MAX_HOURS, `Soat hajmi ${CERTIFICATE.MAX_HOURS} dan oshmasligi kerak`);
export const verifyCodeSchema = z
    .string()
    .regex(CERTIFICATE_VERIFY_CODE_PATTERN, 'Tekshirish kodi noto‘g‘ri');
/**
 * `deletedAt` YO'Q: sertifikat o'chirilmaydi, faqat BEKOR QILINADI.
 *
 * Tarqatilgan QR baribir shu yozuvga olib keladi — hujjat yo'qolsa
 * tekshiruvchi «topilmadi» ni ko'rardi va bu bekor qilinganidan
 * ajralmasdi. Rasmiy hujjatning izi qolishi kerak.
 */
export const certificateSchema = auditableSchema.omit({ deletedAt: true }).extend({
    /** Qog'ozdagi bosilgan seriya raqami (`0000976`) — NOYOB. */
    number: certificateNumberSchema,
    fullName: personNameSchema,
    /**
     * 3×4 surat — MAJBURIY (mijoz qarori §3.3).
     *
     * Shablonda surat uchun ramka bor va u bo'sh qolsa sertifikat
     * tugallanmagan ko'rinadi. Bundan tashqari surat — soxtalikka qarshi
     * asosiy himoya: saytdagi surat qog'ozdagisi bilan solishtiriladi.
     */
    photoMediaId: objectIdSchema,
    courseTitle: z.string().trim().min(3, 'Kurs nomi kamida 3 belgi').max(300),
    hours: certificateHoursSchema,
    /** O'qish muddati — shablondagi «... dan ... gacha» satri. */
    studyStart: z.coerce.date(),
    studyEnd: z.coerce.date(),
    /** Berilgan sana va amal qilish muddati (odatda `issuedAt` + 3 yil). */
    issuedAt: z.coerce.date(),
    expiresAt: z.coerce.date(),
    /** Uzaytirilgan muddat — shablonda alohida satr bor. Bo'lmasa `null`. */
    extendedUntil: z.coerce.date().nullable().default(null),
    status: certificateStatusSchema.default('active'),
    /** Bekor qilish sababi — admin ro'yxatida ko'rinadi, saytga CHIQMAYDI. */
    revokedAt: z.coerce.date().nullable().default(null),
    revokeReason: z.string().trim().max(500).nullable().default(null),
    /**
     * Saytdagi manzildagi tasodifiy kod — NOYOB.
     * Raqamdan yasalmaydi: `/sertifikat/0001` bo'lsa begona odam raqamlarni
     * terib chiqib butun ro'yxatni yig'a olardi (reja §3).
     */
    verifyCode: verifyCodeSchema,
    /** Shakllangan PDF. Hali shakllantirilmagan bo'lsa `null`. */
    pdfMediaId: objectIdSchema.nullable().default(null),
    pdfGeneratedAt: z.coerce.date().nullable().default(null),
});
// ─── Kirish sxemalari ────────────────────────────────────────────────────────
/**
 * Yaratish. `expiresAt` YO'Q: u `issuedAt` + 3 yil bo'lib server tomonda
 * hisoblanadi — shablonda «amal qilish muddati 3 yil» deb bosilgan va
 * qo'lda kiritish ikkovini bir-biriga zid qilib qo'yishi mumkin edi.
 */
export const createCertificateSchema = z
    .object({
    number: certificateNumberSchema,
    fullName: personNameSchema,
    photoMediaId: objectIdSchema,
    courseTitle: z.string().trim().min(3).max(300),
    hours: certificateHoursSchema,
    studyStart: z.coerce.date(),
    studyEnd: z.coerce.date(),
    issuedAt: z.coerce.date(),
})
    .refine((v) => v.studyEnd >= v.studyStart, {
    message: 'O‘qish tugash sanasi boshlanish sanasidan oldin bo‘la olmaydi',
    path: ['studyEnd'],
});
/** Tahrirlash. `verifyCode` o'zgarmaydi: tarqatilgan QR ishlamay qolardi. */
export const updateCertificateSchema = z
    .object({
    number: certificateNumberSchema.optional(),
    fullName: personNameSchema.optional(),
    photoMediaId: objectIdSchema.optional(),
    courseTitle: z.string().trim().min(3).max(300).optional(),
    hours: certificateHoursSchema.optional(),
    studyStart: z.coerce.date().optional(),
    studyEnd: z.coerce.date().optional(),
    issuedAt: z.coerce.date().optional(),
})
    .refine((v) => !v.studyStart || !v.studyEnd || v.studyEnd >= v.studyStart, {
    message: 'O‘qish tugash sanasi boshlanish sanasidan oldin bo‘la olmaydi',
    path: ['studyEnd'],
});
export const extendCertificateSchema = z.object({
    /** Yangi muddat. Berilmasa server amaldagi muddatga 3 yil qo'shadi. */
    extendedUntil: z.coerce.date().optional(),
});
export const revokeCertificateSchema = z.object({
    reason: z.string().trim().max(500).nullable().default(null),
});
export const listCertificatesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(100).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: certificateStatusSchema.optional(),
    /** Raqam yoki F.I.Sh. bo'yicha — FAQAT admin panelda. */
    search: z.string().trim().max(200).optional(),
});
/** Holat: bekor qilinganmi, muddati o'tganmi yoki haqiqiymi. */
export function certificateVerdictOf(input) {
    if (input.status === 'revoked')
        return 'revoked';
    const validUntil = input.extendedUntil ?? input.expiresAt;
    const now = input.now ?? new Date();
    return validUntil.getTime() < now.getTime() ? 'expired' : 'valid';
}
/**
 * Sanaga yillar qo'shish — muddat hisoblashda yagona joy.
 *
 * UTC bo'yicha: sertifikat sanalari kun aniqligida saqlanadi va
 * mahalliy vaqt zonasi hisobga olinsa, natija bir kunga surilib
 * ketishi mumkin edi (PDF va sayt bir xil sanani ko'rsatishi shart).
 */
export function addYears(date, years) {
    const result = new Date(date.getTime());
    result.setUTCFullYear(result.getUTCFullYear() + years);
    return result;
}
//# sourceMappingURL=certificate.js.map